/**
 * Uploads log payload to S3.
 * Supports two modes:
 * 1. Pre-signed URL: set REACT_APP_LOG_PRESIGNED_URL_API (backend returns { uploadUrl, key })
 * 2. AWS SDK: set REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_AWS_REGION, REACT_APP_S3_LOG_BUCKET
 */

const BUCKET = process.env.REACT_APP_S3_LOG_BUCKET;
const REGION = process.env.REACT_APP_AWS_REGION;
const PRESIGNED_API = process.env.REACT_APP_LOG_PRESIGNED_URL_API;
const USE_SDK =
  process.env.REACT_APP_AWS_ACCESS_KEY_ID &&
  process.env.REACT_APP_AWS_SECRET_ACCESS_KEY &&
  BUCKET &&
  REGION;

/**
 * Upload via pre-signed URL. Call your backend to get a PUT URL for the given key.
 * @param {string} key - S3 key e.g. logs/2025-02-24/abc-session.txt
 * @param {string} body - Serialized log content (text or JSON)
 * @param {string} contentType - e.g. 'text/plain' or 'application/json'
 * @returns {Promise<void>}
 */
async function uploadViaPresignedUrl(key, body, contentType) {
  if (!PRESIGNED_API) {
    throw new Error('REACT_APP_LOG_PRESIGNED_URL_API is not set');
  }
  const response = await fetch(PRESIGNED_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, contentType }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Presigned URL API failed: ${response.status} ${text}`);
  }
  const data = await response.json();
  const uploadUrl = data.uploadUrl || data.presignedUrl;
  if (!uploadUrl) throw new Error('Presigned URL API did not return uploadUrl or presignedUrl');
  await fetch(uploadUrl, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': contentType },
  });
}

/**
 * Upload using AWS SDK with credentials from env.
 * @param {string} key - S3 key
 * @param {string} body - Serialized log content
 * @param {string} contentType - MIME type
 */
async function uploadViaSdk(key, body, contentType) {
  const { S3Client, PutObjectCommand } = await import('@aws-sdk/client-s3');
  const client = new S3Client({
    region: REGION,
    credentials: {
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    },
  });
  await client.send(
    new PutObjectCommand({
      Bucket: BUCKET,
      Key: key,
      Body: body,
      ContentType: contentType,
    })
  );
}

/**
 * Upload log batch to S3 at logs/{date}/{sessionId}.txt (or .json).
 * @param {string} sessionId - Unique session identifier
 * @param {string} serialized - Serialized batch (text or JSON)
 * @param {'txt' | 'json'} format - File extension / format
 */
export async function uploadLogsToS3(sessionId, serialized, format = 'txt') {
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const key = `logs/${date}/${sessionId}.${format}`;
  const contentType = format === 'json' ? 'application/json' : 'text/plain';

  if (PRESIGNED_API) {
    await uploadViaPresignedUrl(key, serialized, contentType);
    return;
  }
  if (USE_SDK) {
    await uploadViaSdk(key, serialized, contentType);
    return;
  }
  throw new Error(
    'Log upload not configured: set REACT_APP_LOG_PRESIGNED_URL_API or AWS env vars (REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_AWS_REGION, REACT_APP_S3_LOG_BUCKET)'
  );
}

export function isS3LoggingConfigured() {
  return Boolean(PRESIGNED_API || USE_SDK);
}
